import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router'
import { rpc } from '../api/rpc'
import type { ChatSummary, PromptResponse } from '../api/types'
import { ChatFiles } from '../components/chat/ChatFiles'
import { ChatTodos } from '../components/chat/ChatTodos'
import { Composer } from '../components/chat/Composer'
import { AssistantHeader, MessageItem } from '../components/chat/MessageItem'
import { PromptCard } from '../components/chat/PromptCard'
import { StepGroup } from '../components/chat/StepGroup'
import { SubagentRow } from '../components/chat/SubagentRow'
import { groupFeed, orphanSubagents, type FeedBlock } from '../components/chat/feed'
import { citationsByMessage } from '../components/chat/citations'
import { KbScopePicker } from '../components/chat/KbScopePicker'
import { useChat } from '../components/chat/useChat'
import { useAnalysis } from '../components/chat/useAnalysis'
import { ModelPicker } from '../components/ModelPicker'
import { useMobileBar } from '../components/AppShell'
import { IconEdit, IconGitBranch, IconMore, IconPin, IconPinOff, IconTrash, IconChevronDown } from '../components/icons'
import { Badge } from '../components/ui/Card'
import { Dropdown } from '../components/ui/Popover'
import { useModels } from '../components/useModels'
import { useWorkspaces, WorkspacePicker } from '../components/useWorkspaces'
import { useRepos } from '../components/repos/useRepos'
import { RepoPicker } from '../components/repos/RepoPicker'
import { RepoDock } from '../components/repos/RepoDock'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Field'
import { Modal } from '../components/ui/Modal'
import { useToast } from '../components/ui/Toast'
import { confirmDialog } from '../components/ui/Confirm'

interface Initial { text: string; attachments: { path: string }[] }

/**
 * The first assistant block of a turn carries the "Meow" header row: a turn starts at the user
 * message, so step groups between it and the first assistant text are skipped, not treated as one.
 */
/** True for the first block of an assistant turn (a reply or a step group): it follows a user message. */
function startsTurn(feed: FeedBlock[], index: number): boolean {
  return !isUserBlock(feed[index]) && (index === 0 || isUserBlock(feed[index - 1]))
}

/** The time shown in a turn's header: the first assistant reply of that turn, if there is one yet. */
function turnTime(feed: FeedBlock[], index: number): number | undefined {
  for (let i = index; i < feed.length; i++) {
    const block = feed[i]
    if (block.kind === 'steps') continue
    return block.message.role === 'user' ? undefined : block.message.createdAt
  }
  return undefined
}

/** True for the last assistant reply of a turn: nothing but a user message (or the end) follows it. */
function endsTurn(feed: FeedBlock[], index: number): boolean {
  const next = feed[index + 1]
  return !next || (next.kind === 'message' && next.message.role === 'user')
}

const isUserBlock = (block: FeedBlock | undefined) => block?.kind === 'message' && block.message.role === 'user'

/**
 * Space above a feed block: a clear gap where a turn starts or ends (around a user message), and as
 * little as possible between the steps and replies inside one turn.
 */
function blockGap(feed: FeedBlock[], index: number): string {
  if (index === 0) return ''
  return isUserBlock(feed[index]) || isUserBlock(feed[index - 1]) ? 'mt-7' : 'mt-1'
}

export function ChatPage() {
  const { chatId } = useParams()
  return chatId ? <ChatView key={chatId} chatId={chatId} /> : <NewChat />
}

function NewChat() {
  const { t } = useTranslation()
  const toast = useToast()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { models } = useModels()
  const [model, setModel] = useState('')
  const [ws, setWs] = useState('')
  const [repo, setRepo] = useState<string | null>(null)
  const { items: workspaces } = useWorkspaces()
  const personalId = workspaces.find(w => w.kind === 'personal')?.id
  // The picker follows the chosen workspace: a repository belongs to a workspace, so switching the
  // workspace must not leave a repo from the previous one selected.
  const { items: repos } = useRepos(ws || personalId)
  useEffect(() => {
    if (models?.default && !model) setModel(`${models.default.provider}/${models.default.model}`)
  }, [models, model])
  useEffect(() => { setRepo(null) }, [ws])
  useEffect(() => {
    const r = searchParams.get('repoId')
    const w = searchParams.get('workspaceId')
    if (!r && !w) return
    if (w && ws !== w) {
      setWs(w)
      return
    }
    if (r) setRepo(r)
    navigate('/', { replace: true })
  }, [searchParams, ws, navigate])

  const noModels = models !== null && models.groups.length === 0
  const send = async (text: string, attachments: { path: string }[]) => {
    try {
      const chat = await rpc.call<ChatSummary>('chats.create', { ...(model ? { model } : {}), ...(ws ? { workspaceId: ws } : {}) })
      // Bind before the first turn so the agent starts in the clone, not the workspace root.
      if (repo) await rpc.call('chats.setRepo', { chatId: chat.id, repoId: repo })
      navigate(`/chat/${chat.id}`, { state: { initial: { text, attachments } satisfies Initial } })
      return true
    } catch (err) {
      toast((err as Error).message, 'error')
      return false
    }
  }

  return (
    <div className="mx-auto flex h-full max-w-3xl flex-col justify-center px-4 pt-10 pb-[calc(2.5rem+env(safe-area-inset-bottom,0px))] max-sm:px-2">
      <h1 className="mb-2 text-center font-display text-2xl font-semibold text-ink-100">{t('chat.greeting')}</h1>
      <p className="mb-8 text-center text-sm text-ink-400">{noModels ? t('chat.noModels') : t('chat.greetingSub')}</p>
      {/* The repository and the workspace belong to the message about to be sent, so they sit in the
          composer's own bar (docs/web-ui.md §3) rather than on a row of their own above the card:
          the controls you set before the first turn stay attached to what you type. */}
      <div className="relative rounded-2xl border border-cream-300 bg-cream-50 transition-shadow focus-within:border-ginger-500 focus-within:ring-[3px] focus-within:ring-ginger-100">
        <Composer
          bare
          running={false}
          disabled={noModels}
          onSend={send}
          onStop={() => {}}
          workspaceId={ws || undefined}
          startSlot={repos.length > 0 && (
            <span className="flex min-w-0 items-center gap-1 text-xs text-ink-500">
              <IconGitBranch width={14} height={14} className="shrink-0 text-ink-400" aria-hidden="true" />
              <span className="shrink-0 max-sm:hidden">{t('repos.picker')}:</span>
              <RepoPicker repos={repos} value={repo} onChange={setRepo} />
            </span>
          )}
          endSlot={<WorkspacePicker items={workspaces} value={ws} onChange={setWs} writableOnly inline />}
          modelSlot={<ModelPicker variant="inline" models={models} value={model} onChange={setModel} />}
        />
      </div>
    </div>
  )
}

function ChatView({ chatId }: { chatId: string }) {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const navigate = useNavigate()
  const location = useLocation()
  const { state, loadOlder, reload, resolvePrompt } = useChat(chatId)
  // run_analysis requests of this chat run in this tab (sandboxed iframe).
  useAnalysis(chatId)
  const { models } = useModels()
  const { items: workspaces } = useWorkspaces()
  const personalId = workspaces.find(w => w.kind === 'personal')?.id
  const { items: repos, reload: reloadRepos } = useRepos(state.chat?.workspaceId)
  const setRepo = async (repoId: string | null) => {
    try {
      await rpc.call('chats.setRepo', { chatId, repoId })
      await reload()
      await reloadRepos()
    } catch (err) { toast((err as Error).message, 'error') }
  }
  const [renaming, setRenaming] = useState<string | null>(null)
  const scroller = useRef<HTMLDivElement>(null)
  const stick = useRef(true)
  const sentInitial = useRef(false)

  const send = async (text: string, attachments: { path: string }[]) => {
    try {
      await rpc.call('chats.send', { chatId, text, attachments })
      stick.current = true
      return true
    } catch (err) {
      toast((err as Error).message, 'error')
      return false
    }
  }

  // A chat created from the landing page carries its first message in router state.
  useEffect(() => {
    const initial = (location.state as { initial?: Initial } | null)?.initial
    if (!state.loading && initial && !sentInitial.current) {
      sentInitial.current = true
      navigate(location.pathname, { replace: true, state: null })
      void send(initial.text, initial.attachments)
    }
  })

  const [showScrollDown, setShowScrollDown] = useState(false)

  useLayoutEffect(() => {
    const el = scroller.current
    if (el && stick.current) el.scrollTop = el.scrollHeight
  }, [state.items, state.prompt])

  const onScroll = () => {
    const el = scroller.current
    if (el) {
      const isBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120
      stick.current = isBottom
      if (showScrollDown === isBottom) {
        setShowScrollDown(!isBottom)
      }
    }
  }

  const respond = async (r: PromptResponse) => {
    if (!state.prompt) return
    const { promptId } = state.prompt
    try {
      await rpc.call('chats.respondPrompt', { chatId, promptId, response: r })
      resolvePrompt(promptId)
    } catch (err) {
      toast((err as Error).message, 'error')
      // Already answered elsewhere or expired: resync so a stale card does not linger.
      void reload()
    }
  }

  const call = (method: string, params: object = {}) =>
    rpc.call(method, { chatId, ...params }).catch(err => toast((err as Error).message, 'error'))

  // Skip is the composer's Stop: the user declines to answer and the turn ends,
  // so the tool reports the question as unanswered and a later turn still sees
  // it. The card is cleared locally too — the stop resolves the prompt
  // server-side, and no `done` event may arrive if the turn had already ended.
  const skipPrompt = () => {
    if (state.prompt) resolvePrompt(state.prompt.promptId)
    void call('chats.stop')
  }

  // Esc skips a pending question. Ignored while typing in a field (the composer
  // must keep its own Esc) so a stray keypress cannot kill a turn.
  const pendingQuestionId = state.prompt?.kind === 'question' ? state.prompt.promptId : undefined
  useEffect(() => {
    if (!pendingQuestionId) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      const el = e.target as HTMLElement | null
      if (el && (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el.isContentEditable)) return
      e.preventDefault()
      resolvePrompt(pendingQuestionId)
      void call('chats.stop')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // The listener only lives while one question is pending; `call` and
    // `resolvePrompt` are per-render helpers, so keying on the id avoids churn.
  }, [pendingQuestionId, chatId])

  const chat = state.chat
  const lastUserIndex = state.items.map(i => i.kind === 'message' && i.message.role === 'user').lastIndexOf(true)

  const feed = useMemo(() => groupFeed(state.items, state.running ? state.streamingId : null), [state.items, state.running, state.streamingId])
  // A call is persisted only when it finishes, so a run started before this client
  // loaded (or reloaded) mid-flight has no card in the transcript: without one its
  // tokens would stream into nothing. Give it a card of its own.
  const orphans = useMemo(() => orphanSubagents(state.items, state.subagents), [state.items, state.subagents])
  const citations = useMemo(() => citationsByMessage(state.items), [state.items])
  const streamingMsg = state.running && state.streamingId
    ? state.items.find(i => i.kind === 'message' && i.message.id === state.streamingId)
    : undefined
  const liveThoughtId = streamingMsg?.kind === 'message' && !streamingMsg.message.text ? `${streamingMsg.message.id}:thought` : undefined

  const title = chat ? (chat.title === 'New chat' ? t('chat.untitled') : chat.title) : '…'
  const remove = async () => {
    if (!await confirmDialog(t('chat.confirmDelete'))) return
    await call('chats.delete')
    navigate('/')
  }
  const togglePin = async () => {
    if (!chat) return
    await call('chats.pin', { pinned: !chat.pinnedAt })
  }
  // The files button is shared by the desktop header and the phone top bar (see `useMobileBar`).
  const files = <ChatFiles chatId={chatId} workspaceId={chat?.workspaceId} />
  const menu = (
    <Dropdown
      label={t('common.more')}
      items={[
        { label: t('chat.rename'), icon: <IconEdit width={16} height={16} />, onSelect: () => setRenaming(chat?.title ?? '') },
        {
          label: chat?.pinnedAt ? t('chat.unpin') : t('chat.pin'),
          icon: chat?.pinnedAt ? <IconPinOff width={16} height={16} /> : <IconPin width={16} height={16} />,
          onSelect: () => void togglePin()
        },
        { label: t('common.delete'), icon: <IconTrash width={16} height={16} />, danger: true, divider: true, onSelect: () => void remove() }
      ]}
      trigger={p => (
        <Button {...p} ref={p.ref} variant="ghost" size="icon-sm" aria-label={t('common.more')}><IconMore /></Button>
      )}
    />
  )
  // Phones show one header only: the app's top bar (menu, title, files, actions) replaces this
  // page header, which is hidden below `md` rather than duplicated under the bar.
  useMobileBar({ title, actions: <div className="flex items-center gap-1">{files}{menu}</div> }, [title, chat?.id, chat?.pinnedAt, chat?.workspaceId])

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-line bg-canvas pl-6 pr-4 max-md:hidden">
        <button className="min-w-0 truncate text-left text-[15px] font-semibold text-ink-100 hover:text-ginger-600" onClick={() => setRenaming(chat?.title ?? '')}>
          {title}
        </button>
        {chat && chat.workspaceId && chat.workspaceId !== personalId && <Badge tone="accent">{workspaces.find(w => w.id === chat.workspaceId)?.name ?? t('teams.shared')}</Badge>}
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {files}
          {menu}
        </div>
      </header>

      <div ref={scroller} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex min-h-full flex-col">
          <div className="mx-auto flex w-full max-w-[808px] flex-1 flex-col px-6 pt-8 pb-6 max-sm:px-4">
            {state.hasMore && (
              <div className="mb-6 text-center"><Button variant="ghost" size="sm" onClick={() => void loadOlder()}>{t('chat.loadOlder')}</Button></div>
            )}
            {feed.map((block, index) => (
              <div key={block.kind === 'message' ? block.message.id : block.id} className={blockGap(feed, index)}>
                {block.kind === 'message'
                  ? <MessageItem message={block.message} streaming={block.message.id === state.streamingId && state.running} citations={citations.get(block.message.id)} showHeader={block.message.role === 'assistant' && startsTurn(feed, index)} showActions={endsTurn(feed, index)} />
                  : <>
                      {startsTurn(feed, index) && <div className="mb-1"><AssistantHeader createdAt={turnTime(feed, index)} locale={i18n.language} /></div>}
                      <StepGroup steps={block.steps} liveThoughtId={liveThoughtId} subagents={state.subagents} />
                    </>}
              </div>
            ))}
            {orphans.length > 0 && (
              <section className="mt-2 overflow-hidden rounded-xl border border-line bg-cream-50">
                <ul>
                  {orphans.map(s => (
                    <SubagentRow
                      key={s.taskId}
                      tool={{ id: `live-${s.taskId}`, tool: 'task', input: {}, permission: 'pending' }}
                      subagent={s}
                    />
                  ))}
                </ul>
              </section>
            )}
            {state.running && !state.streamingId && !state.prompt && (
              <p className="mt-2 animate-pulse text-xs text-ink-500">{state.notice ?? t('chat.working')}</p>
            )}
            {/* The task list sits at the end of the feed, like the question card;
                when both exist the question stays below it. */}
            <div className="mt-4 empty:hidden"><ChatTodos todos={state.todos} /></div>
            {state.prompt && <div className="mt-4"><PromptCard prompt={state.prompt} onRespond={respond} onSkip={skipPrompt} /></div>}
            {state.error && (
              <div className="mt-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
                {state.error}
                {lastUserIndex >= 0 && <Button variant="ghost" size="sm" className="ml-2" onClick={() => void call('chats.retry')}>{t('chat.retry')}</Button>}
              </div>
            )}
            {!state.running && !state.error && lastUserIndex >= 0 && lastUserIndex < state.items.length - 1 && (
              <div className="mt-2 flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => void call('chats.retry')}>{t('chat.retry')}</Button>
              </div>
            )}
          </div>

          {/* Phones keep a margin and the card's rounded border: the composer used to run edge to edge
              (`max-sm:px-0` + `max-sm:rounded-none`), which read as a different control than the same
              composer on desktop. `max-sm:px-2` keeps it close to the edges without losing the shape. */}
          <div className="sticky bottom-0 mx-auto w-full max-w-[808px] bg-canvas px-6 pt-2 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] max-sm:px-2">
            {showScrollDown && (
              <div className="absolute left-1/2 -top-10 -translate-x-1/2">
                <Button
                  variant="outline"
                  size="icon-sm"
                  aria-label={t('chat.scrollToEnd')}
                  className="rounded-full bg-cream-50 shadow-pop"
                  onClick={() => {
                    if (scroller.current) {
                      scroller.current.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' })
                      stick.current = true
                      setShowScrollDown(false)
                    }
                  }}
                >
                  <IconChevronDown className="h-4 w-4" />
                </Button>
              </div>
            )}
            {state.queue.length > 0 && (
              <div className="mb-2 space-y-1 px-4 sm:px-0">
                {state.queue.map(q => (
                  <div key={q.id} className="flex items-center gap-2 rounded-lg bg-cream-200/60 px-3 py-1.5 text-xs text-ink-400">
                    <span className="truncate">{t('chat.queued')}: {q.displayText ?? q.text}</span>
                    <button className="ml-auto hover:text-ink-100" onClick={() => void call('chats.queue.remove', { id: q.id })}>×</button>
                  </div>
                ))}
              </div>
            )}
            <div className="relative rounded-2xl border border-cream-300 bg-cream-50 transition-shadow focus-within:border-ginger-500 focus-within:ring-[3px] focus-within:ring-ginger-100">
              {chat && <RepoDock repos={repos} repoId={chat.repoId ?? null} onBind={setRepo} disabled={state.running} />}

              <Composer 
                bare
                running={state.running} 
                onSend={send} 
                onStop={() => void call('chats.stop')} 
                workspaceId={chat?.workspaceId} 
                kbSlot={chat ? <KbScopePicker chatId={chat.id} scope={chat.kbScope} disabled={state.running} /> : undefined}
                modelSlot={
                  <ModelPicker
                    variant="inline"
                    models={models}
                    value={chat?.model ?? ''}
                    disabled={state.running}
                    onChange={m => void call('chats.setModel', { model: m })}
                  />
                }
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={renaming !== null}
        title={t('chat.rename')}
        onClose={() => setRenaming(null)}
        footer={<>
          <Button variant="ghost" onClick={() => setRenaming(null)}>{t('common.cancel')}</Button>
          <Button onClick={async () => { await call('chats.rename', { title: renaming }); setRenaming(null) }} disabled={!renaming?.trim()}>{t('common.save')}</Button>
        </>}
      >
        <Input autoFocus value={renaming ?? ''} onChange={e => setRenaming(e.target.value)} aria-label={t('chat.rename')} />
      </Modal>
    </div>
  )
}
